import React from 'react';
import { 
  Calendar,
  Users,
  MapPin,
  Clock,
  Phone,
  Mail,
  Building2,
  CheckCircle2,
  FileText,
  Plane,
  Hotel,
  Car,
  Ship,
  Utensils,
  Ticket,
  DollarSign
} from 'lucide-react';

const ModernAgencyTemplate = ({ quote, onPayment, isAgentPreview = false }) => {
  if (!quote) {
    return null;
  }

  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    // Parse date in local timezone to avoid UTC offset issues
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Helper function to format time
  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      const date = new Date(`2000-01-01T${timeString}`);
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return timeString;
    }
  };

  // Get icon for component type
  const getComponentIcon = (component) => {
    const type = component.type?.toLowerCase() || '';
    const title = component.title?.toLowerCase() || '';
    
    if (type.includes('flight') || title.includes('flight') || title.includes('air')) {
      return <Plane className="w-5 h-5 text-blue-600" />;
    } else if (type.includes('hotel') || title.includes('hotel') || title.includes('resort')) {
      return <Hotel className="w-5 h-5 text-blue-600" />;
    } else if (type.includes('car') || title.includes('car') || title.includes('rental')) {
      return <Car className="w-5 h-5 text-blue-600" />;
    } else if (type.includes('cruise') || title.includes('cruise') || title.includes('ship')) {
      return <Ship className="w-5 h-5 text-blue-600" />;
    } else if (type.includes('restaurant') || type.includes('dining') || title.includes('dining')) {
      return <Utensils className="w-5 h-5 text-blue-600" />;
    } else if (type.includes('activity') || type.includes('ticket') || type.includes('park')) {
      return <Ticket className="w-5 h-5 text-blue-600" />;
    }
    return <MapPin className="w-5 h-5 text-blue-600" />;
  };

  // Get client name
  const getClientName = () => {
    if (quote.client?.firstName && quote.client?.lastName) {
      return `${quote.client.firstName} ${quote.client.lastName}`;
    }
    return 'Valued Client';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100">
      {/* Header Section - Premium Agency Branding */}
      <div className="bg-white border-b-2 border-blue-600 shadow-sm">
        <div className="max-w-5xl mx-auto px-6 py-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="flex items-center gap-2 text-blue-600 text-sm font-semibold mb-2">
                <Building2 className="w-4 h-4" />
                <span>PROFESSIONAL TRAVEL SERVICES</span>
              </div>
              <h1 className="text-3xl font-bold text-slate-900">
                {quote.quoteTitle || 'Your Travel Itinerary'}
              </h1>
            </div>
            <div className="text-right">
              <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">Quote Reference</div>
              <div className="font-mono text-sm bg-slate-100 px-3 py-1 rounded border border-slate-200">
                {quote.quoteNumber}
              </div>
            </div>
          </div>

          {/* Client & Trip Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-6 border-t border-slate-200">
            <div className="flex items-start gap-3">
              <Users className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wide">Traveler</div>
                <div className="font-semibold text-slate-900">{getClientName()}</div>
              </div>
            </div>
            
            {quote.travelDates?.start && (
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wide">Travel Dates</div>
                  <div className="font-semibold text-slate-900">
                    {formatDate(quote.travelDates.start)}
                    {quote.travelDates.end && ` - ${formatDate(quote.travelDates.end)}`}
                  </div>
                </div>
              </div>
            )}
            
            {(quote.travelers?.adults || quote.travelers?.children?.length) && (
              <div className="flex items-start gap-3">
                <Users className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wide">Party Size</div>
                  <div className="font-semibold text-slate-900">
                    {quote.travelers.adults || 0} Adult{quote.travelers.adults !== 1 ? 's' : ''}
                    {quote.travelers.children?.length > 0 && `, ${quote.travelers.children.length} Child${quote.travelers.children.length !== 1 ? 'ren' : ''}`}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content - Vertical Timeline */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Your Itinerary</h2>
          <p className="text-slate-600">A detailed breakdown of your travel arrangements</p>
        </div>

        {/* Timeline Container */}
        {quote.components && quote.components.length > 0 ? (
          <div className="relative">
            {/* Vertical connecting line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-blue-100"></div>

            {/* Components as timeline items */}
            <div className="space-y-8">
              {quote.components.map((component, index) => (
                <div key={component.id || index} className="relative pl-16">
                  {/* Timeline dot */}
                  <div className="absolute left-3.5 top-6 w-5 h-5 bg-blue-600 rounded-full border-4 border-white shadow-md"></div>

                  {/* Component Card */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
                    {/* Card Header */}
                    <div className="bg-gradient-to-r from-slate-50 to-white px-6 py-4 border-b border-gray-100">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          {getComponentIcon(component)}
                          <div>
                            <h3 className="text-lg font-bold text-slate-900">
                              {component.title || `Component ${index + 1}`}
                            </h3>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="inline-block text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded">
                                {component.type || 'General'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-slate-900">
                            {formatCurrency(component.price)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Card Body - Component Details */}
                    {component.data && Object.keys(component.data).length > 0 && (
                      <div className="px-6 py-5">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {Object.entries(component.data).map(([key, value]) => {
                            if (!value || key === 'isOption' || key === 'optionDescription') return null;
                            
                            // Check if this is a price field and format accordingly
                            const isPriceField = key.toLowerCase().includes('price') || 
                                                 key.toLowerCase().includes('cost') || 
                                                 key.toLowerCase().includes('fee') ||
                                                 key.toLowerCase().includes('total') ||
                                                 key.toLowerCase().includes('deposit');
                            const isNumeric = !isNaN(parseFloat(value)) && isFinite(value);
                            const isConfirmation = key.toLowerCase().includes('confirmation') || 
                                                   key.toLowerCase().includes('reference') ||
                                                   key.toLowerCase().includes('booking');
                            
                            let displayValue = String(value);
                            if (isPriceField && isNumeric) {
                              displayValue = formatCurrency(parseFloat(value));
                            }
                            
                            return (
                              <div key={key} className="flex flex-col">
                                <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">
                                  {key.replace(/_/g, ' ')}
                                </div>
                                <div className={`text-slate-900 ${isConfirmation ? 'font-mono text-xs bg-gray-50 p-2 rounded border border-gray-200' : 'font-semibold'}`}>
                                  {displayValue}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Component Photos */}
                    {component.photos && component.photos.length > 0 && (
                      <div className="px-6 pb-5">
                        <h4 className="text-sm font-bold text-slate-900 mb-3 uppercase tracking-wide flex items-center gap-2">
                          <span>📸</span> Photos
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {component.photos.map((photo, photoIndex) => (
                            <div 
                              key={photoIndex} 
                              className="relative group cursor-pointer rounded-lg overflow-hidden shadow hover:shadow-md transition-shadow border border-slate-200 bg-white"
                              onClick={() => window.open(photo.url, '_blank')}
                            >
                              <img 
                                src={photo.url} 
                                alt={photo.name || 'Component photo'} 
                                className="w-full h-auto object-contain"
                                style={{ maxHeight: '300px' }}
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2">
                                <span className="text-white text-sm font-medium">{photo.name || 'View Photo'}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Component Documents */}
                    {component.documents && component.documents.length > 0 && (
                      <div className="px-6 pb-5">
                        <h4 className="text-sm font-bold text-slate-900 mb-3 uppercase tracking-wide flex items-center gap-2">
                          <span>📋</span> Documents
                        </h4>
                        <div className="space-y-2">
                          {component.documents.map((doc, docIndex) => (
                            <a 
                              key={docIndex}
                              href={doc.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-3 bg-slate-50 hover:bg-slate-100 px-4 py-2 rounded transition-colors border border-slate-200"
                            >
                              <span className="text-lg">📄</span>
                              <span className="text-slate-900 text-sm font-medium">{doc.name}</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No components added to this itinerary yet</p>
          </div>
        )}

        {/* Notes Section */}
        {quote.notes && (
          <div className="mt-12 bg-blue-50 rounded-xl border border-blue-100 p-6">
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Important Information</h3>
                <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">
                  {quote.notes}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Pricing Summary - Professional Box */}
        <div className="mt-12 bg-white rounded-xl shadow-md border-2 border-slate-200 overflow-hidden">
          <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-6 py-4">
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Investment Summary
            </h3>
          </div>
          
          <div className="p-6">
            <div className="space-y-4">
              <div className="flex justify-between items-center pb-4 border-b border-slate-200">
                <span className="text-slate-600 font-medium">Total Investment</span>
                <span className="text-3xl font-bold text-slate-900">
                  {formatCurrency(quote.totalPrice)}
                </span>
              </div>
              
              {quote.depositAmount > 0 && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600 font-medium">Initial Deposit Required</span>
                    <span className="text-2xl font-bold text-blue-600">
                      {formatCurrency(quote.depositAmount)}
                    </span>
                  </div>
                  
                  {quote.depositDueDate && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-slate-500">Deposit Due Date</span>
                      <span className="font-semibold text-slate-700">
                        {formatDate(quote.depositDueDate)}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Payment Button */}
            {onPayment && (
              <div className="mt-6 pt-6 border-t border-slate-200">
                <button
                  onClick={onPayment}
                  disabled={isAgentPreview}
                  className={`w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-6 rounded-lg transition-all duration-200 flex items-center justify-center gap-3 ${
                    isAgentPreview ? 'opacity-50 cursor-not-allowed' : 'shadow-lg hover:shadow-xl'
                  }`}
                >
                  <CheckCircle2 className="w-5 h-5" />
                  {isAgentPreview ? 'Preview Mode - Payment Disabled' : 'Proceed to Secure Payment'}
                </button>
                {!isAgentPreview && (
                  <p className="text-center text-xs text-slate-500 mt-3">
                    Secure payment processing • Your information is protected
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Agent Contact Footer */}
        {(quote.agent_name || quote.agent_email || quote.agent_phone) && (
          <div className="mt-12 bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-slate-700 to-slate-600 px-6 py-4">
              <div className="flex items-center justify-center gap-2 text-white">
                <Phone className="w-5 h-5" />
                <h3 className="text-lg font-bold">Your Travel Advisor</h3>
              </div>
            </div>
            
            <div className="p-6">
              {quote.agent_name && (
                <div className="text-center mb-4">
                  <p className="text-xl font-bold text-slate-900">{quote.agent_name}</p>
                  {quote.agent_business_name && (
                    <p className="text-slate-600 font-medium">{quote.agent_business_name}</p>
                  )}
                </div>
              )}
              
              <div className="space-y-3 max-w-md mx-auto">
                {quote.agent_email && (
                  <a 
                    href={`mailto:${quote.agent_email}`}
                    className="flex items-center gap-3 bg-slate-50 px-4 py-3 rounded-lg hover:bg-slate-100 transition-colors border border-slate-200"
                  >
                    <Mail className="w-5 h-5 text-blue-600" />
                    <span className="text-slate-900 font-medium">{quote.agent_email}</span>
                  </a>
                )}
                
                {quote.agent_phone && (
                  <a 
                    href={`tel:${quote.agent_phone}`}
                    className="flex items-center gap-3 bg-slate-50 px-4 py-3 rounded-lg hover:bg-slate-100 transition-colors border border-slate-200"
                  >
                    <Phone className="w-5 h-5 text-blue-600" />
                    <span className="text-slate-900 font-medium">{quote.agent_phone}</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer Branding */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-2 text-slate-400 text-sm">
            <Building2 className="w-4 h-4" />
            <span>Professional Travel Services • Trusted Since Day One</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModernAgencyTemplate;
